import { format } from 'date-fns'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type PDFOrientation = 'portrait' | 'landscape'

/**
 * Exports data as a PDF file with table format and downloads it
 * @param data - Array of objects to export (each object will be a row)
 * @param title - PDF title/filename
 * @param team - Optional team name to display at the top of the document
 * @param orientation - Page orientation ('landscape' or 'portrait')
 */
const downloadPDF = <T extends Record<string, any>>(
  data: T[],
  title: string,
  team: string = '',
  orientation: PDFOrientation = 'landscape'
): void => {
  if (!data || data.length === 0) {
    /* console.warn('No data to export to PDF') */
    return
  }

  const doc = new jsPDF(orientation)
  const pageSize = doc.internal.pageSize
  const pageHeight = pageSize.getHeight()
  const pageWidth = pageSize.getWidth()

  // Add team name centered at the top
  doc.text(team, pageWidth / 2 - doc.getTextWidth(team) / 2, 10)

  // Add title
  doc.text(title, 15, 20)

  // Add footer with date
  const footer = format(new Date(), 'dd-MM-yyyy HH:mm')
  doc.text(
    footer,
    pageWidth / 2 - doc.getTextWidth(footer) / 2,
    pageHeight - 10
  )

  const headers = Object.keys(data[0])

  autoTable(doc, {
    startY: 30,
    head: [headers],
    columns: headers.map(h => ({ header: h, dataKey: h })),
    didParseCell: data => {
      const financialOrNumericColumns = [
        'Net Surface',
        'Rentable surface',
        'Potential rent',
        'Loyer potentiel',
        'Actual rent',
        'Loyer actuel',
        'Surface area',
        'Surface',
        'Surface nette',
        'Surface louable',
        'Parking units',
        'Unités de parking',
        'Parking',
        'Rent',
        'Loyer',
        'Occupancy',
        'Occupancy rate',
        "Taux d'occupation",
        'Occupation',
        'Balance',
        'Solde',
        'Total',
        'Total (AT)',
        'Total (TTC)'
      ]

      const cellRawValue = data.cell.raw as string
      const isRightAligned =
        financialOrNumericColumns.includes(cellRawValue) ||
        (typeof cellRawValue === 'string' &&
          /^[0-9.-]*$/.test(cellRawValue.replace(/\s+/g, '')) &&
          cellRawValue.replace(/\s+/g, '').indexOf('-') ===
            cellRawValue.replace(/\s+/g, '').lastIndexOf('-'))

      if (isRightAligned) {
        data.cell.styles.halign = 'right'
      }
    },
    body: data
  })

  doc.save(title)
}

export default downloadPDF
